import { keyframes } from '@emotion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  buildAppsFlyerDeeplink,
  buildAppsFlyerRedirectUrl,
} from '@/utils/deeplink';
import { IosSettingsIcon, MiniIphoneIcon } from '@/components/ui/IosReferenceIcons';
import findMyIconUrl from '@/assets/icons/find-my.svg?url';
import handRaisedFillUrl from '@/assets/sf-symbols/hand.raised.fill.png?url';
import keyFillUrl from '@/assets/sf-symbols/key.fill.png?url';
import locationFillUrl from '@/assets/sf-symbols/location.fill.png?url';
import lockFillUrl from '@/assets/sf-symbols/lock.fill.png?url';

const IOS_SYSTEM_BLUE = '#007aff';
const IOS_SYSTEM_BLUE_DARK = '#0a84ff';
const IOS_SYSTEM_GREEN_DARK = '#30d158';
const IOS_SYSTEM_RED = '#ff3b30';
const IOS_SYSTEM_RED_DARK = '#ff453a';
const IOS_SYSTEM_GRAY = '#8e8e93';
const IOS_SYSTEM_BACKGROUND_DARK = '#000000';
const IOS_LABEL_LIGHT = '#000000';
const IOS_SECONDARY_LABEL_LIGHT = 'rgba(60, 60, 67, 0.60)';
const IOS_SEPARATOR_LIGHT = 'rgba(60, 60, 67, 0.18)';
const IOS_SYSTEM_GRAY_5_LIGHT = '#e5e5ea';
const IOS_LABEL_DARK = '#ffffff';
const IOS_SECONDARY_LABEL_DARK = 'rgba(235, 235, 245, 0.72)';
const IOS_TERTIARY_LABEL_DARK = 'rgba(235, 235, 245, 0.60)';
const IOS_FONT_TEXT = '-apple-system, BlinkMacSystemFont, \'SF Pro Text\', \'SF Pro Display\', \'Helvetica Neue\', Arial, sans-serif';
const IOS_FONT_DISPLAY = '-apple-system, BlinkMacSystemFont, \'SF Pro Display\', \'SF Pro Text\', \'Helvetica Neue\', Arial, sans-serif';
const INSTALL_VERSION = 'v7';
const INITIAL_COUNTDOWN_SECONDS = 59;
const ALERT_REOPEN_DELAY_MS = 280;
const ALERT_EXIT_DURATION_MS = 190;
const DEVICE_FALLBACK_LABEL = 'Not defined';

const IPHONE_DEVICE_PROFILES = [
  { width: 440, height: 956, dpr: 3, label: '16/17 Pro Max', exact: true },
  { width: 402, height: 874, dpr: 3, label: '16 Pro', exact: true },
  { width: 430, height: 932, dpr: 3, label: '14/15 Pro Max', exact: false },
  { width: 393, height: 852, dpr: 3, label: '14/15 Pro', exact: false },
  { width: 428, height: 926, dpr: 3, label: '12/13/14 Plus', exact: false },
  { width: 390, height: 844, dpr: 3, label: '12/13/14', exact: false },
  { width: 414, height: 896, dpr: 3, label: 'XS Max / 11 Pro Max', exact: false },
  { width: 414, height: 896, dpr: 2, label: '11 / XR', exact: false },
  { width: 375, height: 812, dpr: 3, label: 'X / XS / 11 Pro', exact: false },
  { width: 375, height: 667, dpr: 2, label: 'SE / 8', exact: false },
  { width: 320, height: 568, dpr: 2, label: 'SE', exact: true },
] as const;

function getNormalizedPair(width: number, height: number) {
  return {
    height: Math.max(Math.round(width), Math.round(height)),
    width: Math.min(Math.round(width), Math.round(height)),
  };
}

function isLikelyIphoneClient(): boolean {
  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';

  return /\biPhone\b/i.test(userAgent) || platform === 'iPhone';
}

function getDeviceModelLabel(): string {
  if (typeof window === 'undefined') {
    return DEVICE_FALLBACK_LABEL;
  }

  if (!isLikelyIphoneClient()) {
    return DEVICE_FALLBACK_LABEL;
  }

  const viewport = getNormalizedPair(window.innerWidth, window.innerHeight);
  const screenSize = getNormalizedPair(window.screen.width, window.screen.height);
  const dpr = window.devicePixelRatio || 1;

  const scoredProfiles = IPHONE_DEVICE_PROFILES.map((profile) => {
    const viewportScore = Math.abs(profile.width - viewport.width)
      + Math.abs(profile.height - viewport.height);
    const screenScore = Math.abs(profile.width - screenSize.width)
      + Math.abs(profile.height - screenSize.height);
    const dimensionScore = Math.min(viewportScore, screenScore);
    const dprScore = Math.abs(profile.dpr - dpr) * 16;

    return {
      exact: profile.exact,
      label: profile.label,
      score: dimensionScore + dprScore,
    };
  });

  scoredProfiles.sort((left, right) => left.score - right.score);
  const bestProfile = scoredProfiles[0];

  return bestProfile?.exact && bestProfile.score <= 10
    ? bestProfile.label
    : DEVICE_FALLBACK_LABEL;
}

function redirectToInstallVersion(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const deeplink = buildAppsFlyerDeeplink({
    paywallVersion: INSTALL_VERSION,
  });
  const redirectUrl = buildAppsFlyerRedirectUrl(
    null,
    deeplink,
    'install_v1',
  );

  window.location.href = redirectUrl;
}

const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${IOS_SYSTEM_BACKGROUND_DARK};
`;

const Screen = styled.main`
  --content-width: min(378px, calc(100% - 52px));
  --content-edge: calc((100% - var(--content-width)) / 2);
  --alert-width: min(312px, calc(100% - 48px));
  --alert-edge: calc((100% - var(--alert-width)) / 2);
  position: relative;
  width: 100%;
  max-width: 430px;
  min-height: 100svh;
  margin: 0 auto;
  overflow: hidden;
  background: ${IOS_SYSTEM_BACKGROUND_DARK};
  color: ${IOS_LABEL_DARK};
  color-scheme: dark;
  font-family: ${IOS_FONT_TEXT};
`;

const Nav = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 74px;
  padding: 28px 34px 0;
`;

const Title = styled.h1`
  margin: 0;
  color: ${IOS_SECONDARY_LABEL_DARK};
  font-family: ${IOS_FONT_DISPLAY};
  font-size: 18px;
  line-height: 1.2;
  font-weight: 600;
`;

const Content = styled.section`
  padding: 0 var(--content-edge) 36px;
`;

const SectionLabel = styled.p`
  margin: 0 0 20px;
  color: ${IOS_SYSTEM_GRAY};
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
`;

const DeviceWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 50px;
`;

const DeviceFigure = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DeviceModelName = styled.div`
  margin-top: 7px;
  width: 100%;
  color: ${IOS_TERTIARY_LABEL_DARK};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
`;

const SecurityList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const SecurityRow = styled.li`
  display: grid;
  grid-template-columns: 29px minmax(0, 1fr) max-content;
  align-items: center;
  gap: 10px;
`;

const SYMBOL_URLS = {
  hand: handRaisedFillUrl,
  key: keyFillUrl,
  location: locationFillUrl,
  lock: lockFillUrl,
} as const;

type SymbolName = keyof typeof SYMBOL_URLS;

const AppTile = styled.span<{ $color: string }>`
  display: grid;
  place-items: center;
  width: 29px;
  height: 29px;
  border-radius: 7px;
  background: ${({ $color }) => $color};
  color: ${IOS_LABEL_DARK};
  box-shadow: 0 7px 18px rgba(0, 0, 0, 0.28);
`;

const SymbolMask = styled.span<{ $url: string }>`
  width: 18px;
  height: 18px;
  display: block;
  background: currentColor;
  mask: url(${({ $url }) => $url}) center / contain no-repeat;
  -webkit-mask: url(${({ $url }) => $url}) center / contain no-repeat;
`;

const FindMyTile = styled.img`
  display: block;
  width: 29px;
  height: 29px;
  border-radius: 7px;
  box-shadow: 0 7px 18px rgba(0, 0, 0, 0.28);
`;

const RowLabel = styled.span`
  min-width: 0;
  color: ${IOS_SECONDARY_LABEL_DARK};
  font-size: 18px;
  font-weight: 500;
`;

const RowStatus = styled.span`
  color: ${IOS_SYSTEM_RED_DARK};
  font-size: 15px;
  font-weight: 500;
`;

const DialogOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  pointer-events: none;
`;

const dialogEnter = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.82);
  }

  62% {
    opacity: 1;
    transform: scale(1.018);
  }

  82% {
    opacity: 1;
    transform: scale(0.996);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const dialogExit = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }

  45% {
    opacity: 0.74;
    transform: scale(0.985);
  }

  100% {
    opacity: 0;
    transform: scale(0.94);
  }
`;

const InstallDialog = styled.div<{ $closing: boolean }>`
  --install-alert-bg: ${IOS_SYSTEM_GRAY_5_LIGHT};

  position: absolute;
  top: clamp(188px, 21svh, 204px);
  left: var(--alert-edge);
  width: var(--alert-width);
  overflow: hidden;
  border-radius: 14px;
  background: var(--install-alert-bg);
  color: ${IOS_LABEL_LIGHT};
  color-scheme: light;
  font-family: ${IOS_FONT_TEXT};
  text-align: center;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.36);
  transform: scale(1);
  animation: ${({ $closing }) => (
    $closing
      ? `${dialogExit} ${ALERT_EXIT_DURATION_MS}ms cubic-bezier(0.32, 0, 0.67, 0) both`
      : `${dialogEnter} 360ms cubic-bezier(0.16, 1, 0.3, 1) both`
  )};
  transform-origin: center center;
  will-change: ${({ $closing }) => ($closing ? 'opacity, transform' : 'auto')};
  pointer-events: ${({ $closing }) => ($closing ? 'none' : 'auto')};

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1ms;
  }
`;

const InstallDialogBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 25px 14px 17px;
  background: var(--install-alert-bg);
`;

const DialogAppIconWrap = styled.div`
  position: relative;
  margin-bottom: 6px;
  background: transparent;
  line-height: 0;
`;

const Countdown = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  min-inline-size: 5ch;
  margin-bottom: 6px;
  color: ${IOS_SYSTEM_RED};
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  background: var(--install-alert-bg);
  box-shadow: 0 0 0 2px var(--install-alert-bg);
  opacity: 1;
  text-decoration: none;
  user-select: none;
  -webkit-text-fill-color: ${IOS_SYSTEM_RED};
  -webkit-touch-callout: none;
  -webkit-user-select: none;
`;

const InstallDialogTitle = styled.h2`
  margin: 0 0 6px;
  max-width: 232px;
  color: ${IOS_LABEL_LIGHT};
  font-family: ${IOS_FONT_DISPLAY};
  font-size: 17px;
  line-height: 1.22;
  font-weight: 600;
`;

const InstallDialogText = styled.p`
  margin: 0;
  max-width: 238px;
  color: ${IOS_SECONDARY_LABEL_LIGHT};
  font-size: 13px;
  line-height: 1.24;
`;

const DialogActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid ${IOS_SEPARATOR_LIGHT};
`;

const DialogButton = styled.button<{ $tone?: 'cancel' }>`
  min-height: 49px;
  border: 0;
  border-right: 1px solid ${IOS_SEPARATOR_LIGHT};
  background: transparent;
  color: ${({ $tone }) => ($tone === 'cancel' ? IOS_SYSTEM_RED : IOS_SYSTEM_BLUE)};
  font: inherit;
  font-size: 17px;
  font-weight: 400;
  cursor: pointer;

  &:last-of-type {
    border-right: 0;
  }

  &:active {
    background: rgba(60, 60, 67, 0.12);
  }

  &:focus-visible {
    outline: 3px solid rgba(0, 122, 255, 0.45);
    outline-offset: -3px;
  }
`;

function formatCountdown(seconds: number): string {
  return `00:${seconds.toString().padStart(2, '0')}`;
}

function SymbolIcon({ name }: { name: SymbolName }) {
  return <SymbolMask $url={SYMBOL_URLS[name]} aria-hidden="true" />;
}

export function InstallV1() {
  const [deviceModelLabel] = useState(getDeviceModelLabel);
  const [alertVisible, setAlertVisible] = useState(true);
  const [alertClosing, setAlertClosing] = useState(false);
  const [countdown, setCountdown] = useState(INITIAL_COUNTDOWN_SECONDS);
  const [processing, setProcessing] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const reopenTimerRef = useRef<number | null>(null);

  const closeAlertAndReopen = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (reopenTimerRef.current !== null) {
      window.clearTimeout(reopenTimerRef.current);
      reopenTimerRef.current = null;
    }

    setAlertClosing(true);
    hideTimerRef.current = window.setTimeout(() => {
      setAlertVisible(false);
      setAlertClosing(false);
      hideTimerRef.current = null;
      reopenTimerRef.current = window.setTimeout(() => {
        setCountdown(INITIAL_COUNTDOWN_SECONDS);
        setAlertVisible(true);
        reopenTimerRef.current = null;
      }, ALERT_REOPEN_DELAY_MS);
    }, ALERT_EXIT_DURATION_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }

      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
      }

      if (reopenTimerRef.current !== null) {
        window.clearTimeout(reopenTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!alertVisible || alertClosing || processing) {
      return undefined;
    }

    intervalRef.current = window.setInterval(() => {
      setCountdown((previousSeconds) => {
        return Math.max(previousSeconds - 1, 0);
      });
    }, 1000);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [alertClosing, alertVisible, processing]);

  useEffect(() => {
    if (!alertVisible || alertClosing || processing || countdown > 0) {
      return undefined;
    }

    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const closeTimer = window.setTimeout(closeAlertAndReopen, 0);

    return () => {
      window.clearTimeout(closeTimer);
    };
  }, [alertClosing, alertVisible, closeAlertAndReopen, countdown, processing]);

  const onCancel = () => {
    if (alertClosing) {
      return;
    }

    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    closeAlertAndReopen();
  };

  const onInstall = () => {
    if (processing || alertClosing) {
      return;
    }

    setProcessing(true);
    redirectToInstallVersion();
  };

  return (
    <Page>
      <Screen aria-label="Device security status">
        <Nav>
          <Title>Device Security</Title>
        </Nav>

        <Content>
          <SectionLabel>Device Type</SectionLabel>
          <DeviceWrap>
            <DeviceFigure>
              <MiniIphoneIcon size="md" tone="red" />
              <DeviceModelName>{deviceModelLabel}</DeviceModelName>
            </DeviceFigure>
          </DeviceWrap>

          <SecurityList>
            <SecurityRow>
              <AppTile $color={IOS_SYSTEM_GRAY}>
                <SymbolIcon name="key" />
              </AppTile>
              <RowLabel>Protected</RowLabel>
              <RowStatus>Compromised</RowStatus>
            </SecurityRow>
            <SecurityRow>
              <AppTile $color={IOS_SYSTEM_GRAY}>
                <SymbolIcon name="location" />
              </AppTile>
              <RowLabel>Location</RowLabel>
              <RowStatus>Compromised</RowStatus>
            </SecurityRow>
            <SecurityRow>
              <FindMyTile src={findMyIconUrl} aria-hidden="true" alt="" draggable={false} />
              <RowLabel>Find My</RowLabel>
              <RowStatus>Compromised</RowStatus>
            </SecurityRow>
            <SecurityRow>
              <AppTile $color={IOS_SYSTEM_GREEN_DARK}>
                <SymbolIcon name="lock" />
              </AppTile>
              <RowLabel>Passcode</RowLabel>
              <RowStatus>Compromised</RowStatus>
            </SecurityRow>
            <SecurityRow>
              <AppTile $color={IOS_SYSTEM_BLUE_DARK}>
                <SymbolIcon name="hand" />
              </AppTile>
              <RowLabel>Privacy & Security</RowLabel>
              <RowStatus>Compromised</RowStatus>
            </SecurityRow>
          </SecurityList>
        </Content>

        {alertVisible && (
          <DialogOverlay role="presentation">
            <InstallDialog
              $closing={alertClosing}
              role="dialog"
              aria-modal="true"
              aria-labelledby="install-v1-title"
              aria-describedby="install-v1-description"
            >
              <InstallDialogBody>
                <DialogAppIconWrap aria-hidden="true">
                  <IosSettingsIcon
                    badge
                    badgeShadow={false}
                    shadow={false}
                    size="md"
                    trimTransparentGutter
                  />
                </DialogAppIconWrap>
                <Countdown aria-hidden="true">
                  {formatCountdown(countdown)}
                </Countdown>
                <InstallDialogTitle id="install-v1-title">
                  Your iPhone is infected with trojan virus!
                </InstallDialogTitle>
                <InstallDialogText id="install-v1-description">
                  Immediately install the recommended security application
                </InstallDialogText>
              </InstallDialogBody>
              <DialogActions>
                <DialogButton type="button" $tone="cancel" onClick={onCancel}>
                  Cancel
                </DialogButton>
                <DialogButton type="button" onClick={onInstall}>
                  Install
                </DialogButton>
              </DialogActions>
            </InstallDialog>
          </DialogOverlay>
        )}
      </Screen>
    </Page>
  );
}
